import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import throttle from 'lodash/throttle';

import { analytics } from '../../services/analytics';

// We assume that this time is enough to see all games in the viewport
export const SEEN_TIME = 2000;
// When a session ends, we should send all the data about the games that the user has seen to the backend.
const SESSION_TIME = 5000;

type GamePositionSchema = {
  id: string;
  category: string;
  vertPos: number;
  horPos: number;
};

const seenGames = new Map<string, GamePositionSchema>();

function getViewPort() {
  return {
    top: document.documentElement.scrollTop,
    bottom: document.documentElement.scrollTop + window.innerHeight,
    left: document.documentElement.scrollLeft,
    right: document.documentElement.scrollLeft + window.innerWidth,
  };
}

const sendSeenGames = debounce(() => {
  const gamesToSend = Array.from(seenGames.entries()).reduce<AnalyticsEvents['games_impression']>(
    (acc, [id, game]) => {
      acc.slot_id.push(id);
      acc.category.push(game.category);
      acc.vertical_location.push(game.vertPos);
      acc.horizontal_location.push(game.horPos);
      return acc;
    },
    {
      slot_id: [],
      category: [],
      vertical_location: [],
      horizontal_location: [],
    },
  );
  if (!isEmpty(gamesToSend.slot_id)) {
    void analytics.sendEvent('games_impression', gamesToSend);
  }
  seenGames.clear();
}, SESSION_TIME);

function gridListener() {
  const viewPort = getViewPort();
  const row = document.querySelector<HTMLDivElement>('[data-games-row]');
  if (!row) {
    return;
  }
  const games = Array.from(row.querySelectorAll<HTMLDivElement>(`[data-game-id]`));
  if (!games.length) {
    return;
  }
  const category = row.getAttribute('data-games-row')!;
  let currOffset = row.offsetTop;
  let vertPos = 1;
  let horPos = 1;
  for (let i = 0; i < games.length; i++) {
    const game = games[i];
    if (game.offsetTop > viewPort.bottom) {
      // If the game is below the viewport, we can stop the loop
      break;
    }
    if (currOffset < game.offsetTop) {
      vertPos += 1;
      horPos = 1;
      currOffset = game.offsetTop;
    }
    const top = game.getBoundingClientRect().top + window.scrollY;
    const height = game.getBoundingClientRect().height;
    // We should consider a game as "seen" only if at least 85% of its height is visible.
    const visibleHeight = Math.min(viewPort.bottom, top + height) - Math.max(viewPort.top, top);
    if ((visibleHeight / height) * 100 < 85) {
      continue;
    }
    const id = game.getAttribute('data-game-id');
    if (!id) {
      // If the game doesn't have an id, something is wrong, so we should stop the loop
      throw new Error('data-game-id is not set');
    }
    seenGames.set(id, {
      id,
      vertPos,
      horPos: horPos,
      category,
    });
    horPos += 1;
  }

  sendSeenGames();
}

function lobbyListener() {
  const viewPort = getViewPort();
  Array.from(document.querySelectorAll<HTMLDivElement>('[data-games-row]'))
    .filter((row) => {
      const top = row.getBoundingClientRect().top + window.scrollY;
      const bottom = row.getBoundingClientRect().bottom + window.scrollY;
      if (top > viewPort.top && bottom < viewPort.bottom) {
        return true;
      }
      // We should consider a game as "seen" only if at least 75% of its row height is visible.
      const height = bottom - top;
      const visibleHeight = Math.min(viewPort.bottom, bottom) - Math.max(viewPort.top, top);
      return (visibleHeight / height) * 100 > 75;
    })
    .forEach((row, rowIdx) => {
      const rowLeft = row.getBoundingClientRect().left + window.scrollX;
      const rowRight = row.getBoundingClientRect().right + window.scrollX;
      const games = Array.from(row.querySelectorAll<HTMLDivElement>(`[data-game-id]`));
      if (!games.length) {
        return;
      }
      const category = row.getAttribute('data-games-row')!;
      for (let i = 0; i < games.length; i++) {
        const game = games[i];
        const right = game.getBoundingClientRect().right + window.scrollX;
        // Skip games that are not visible
        if (right < rowLeft) {
          continue;
        }
        // Since games are sorted by horizontal position, we can break the loop here
        const left = game.getBoundingClientRect().left + window.scrollX;
        if (left > rowRight) {
          break;
        }
        // We should consider a game as "seen" only if at least 75% of its width is visible.
        const width = game.getBoundingClientRect().width;
        const visibleWidth = Math.min(rowRight, right) - Math.max(rowLeft, left);
        if ((visibleWidth / width) * 100 < 75) {
          continue;
        }
        const id = game.getAttribute('data-game-id');
        if (!id) {
          throw new Error('data-game-id is not set');
        }
        seenGames.set(id, {
          id,
          vertPos: rowIdx + 1,
          horPos: i + 1,
          category,
        });
      }
    });
  sendSeenGames();
}

// We assume that a user has seen the games within the initial viewport before scrolling down (leading).
export const seenGamesGridListener = throttle(gridListener, SEEN_TIME, { leading: true, trailing: true });
export const seenGamesLobbyListener = throttle(lobbyListener, SEEN_TIME, { leading: true, trailing: true });
